import { PaymentMethod, PaymentMethodType } from 'types';

export interface GetAmountAndBalance<T> {
  formValues: T;
  saleTotal: number;
  creditProccessingFees: number;
}

export const calculateBalance = ({
  saleTotal,
  paymentMethods,
  creditProccessingFees,
}: {
  saleTotal: number;
  creditProccessingFees: number;
  paymentMethods: PaymentMethod[];
}) => {
  const paymentMethodsTotal = paymentMethods.reduce(
    (acc, item) => acc + +item.amount,
    0
  );

  return +(saleTotal - paymentMethodsTotal - creditProccessingFees).toFixed(2);
};

// Owner 19 Oct 2026: a check can be written for more than the amount due.
// The check line keeps the full check (it is what goes to the bank) and the
// change handed back is a cash line of minus the change, received $0.00:
// the shape checkout 21070 was corrected to ($212.00 check + -$1.06 cash).
export const CHECK_CHANGE_ID_SUFFIX = '-change';

export const getCheckChangeLineId = (checkId: PaymentMethod['id']) =>
  `${checkId}${CHECK_CHANGE_ID_SUFFIX}`;

/** The change due on a check of `checkAmount` when `remainder` is due. */
export const getCheckChange = (checkAmount: number, remainder: number) =>
  remainder > 0 && checkAmount > remainder
    ? Number((checkAmount - remainder).toFixed(2))
    : 0;

/** A cash line below zero is change handed back (check written over). */
export const isChangeLine = (method: PaymentMethod) =>
  method.type === PaymentMethodType.Cash && Number(method.amount) < 0;

export const calculateChange = (paymentMethods: PaymentMethod[] = []) => {
  const cashPayments = paymentMethods.filter(
    (method) => method.type === PaymentMethodType.Cash
  );

  // Lines from the API carry receivedCashAmount as a string ("1.00"), and a
  // change line carries "0.00": count every cash line that has the field.
  if (
    !cashPayments.some(
      (method) =>
        method.receivedCashAmount !== null &&
        method.receivedCashAmount !== undefined
    )
  ) {
    return 0;
  }

  const totalReceivedCashAmount = cashPayments.reduce(
    (acc, payment) => acc + Number(payment.receivedCashAmount ?? 0),
    0
  );

  const totalCashAmount = cashPayments.reduce(
    (acc, payment) => acc + Number(payment.amount),
    0
  );

  return Math.max(
    Number((totalReceivedCashAmount - totalCashAmount).toFixed(2)),
    0
  );
};

export const getAmountAndBalance = <T extends Record<string, any>>({
  saleTotal,
  formValues,
  creditProccessingFees,
}: GetAmountAndBalance<T>) => {
  const balance = calculateBalance({
    saleTotal,
    creditProccessingFees,
    paymentMethods: formValues.paymentMethods,
  });

  const hasSalesAndFullAmount = formValues?.commissions?.length && !balance;

  return { balance, hasSalesAndFullAmount };
};
